// src/components/hooks/usePushToken.ts

import { useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { supabase } from '@/lib/supabaseClient';
import * as Sentry from '@sentry/react';

// ─── Registra (o actualiza) el push_token del usuario logueado ──────────────
// Solo debe llamarse cuando hay sesión activa (userEmail !== null).
// Guests (sin login) nunca deben invocar este hook.

export const usePushToken = () => {
  const registrarPushToken = useCallback(async (idUsuario: string, email: string) => {
    // Solo tiene sentido en la app nativa (Capacitor), no en navegador web
    if (!Capacitor.isNativePlatform()) return;

    try {
      const permStatus = await PushNotifications.checkPermissions();

      if (permStatus.receive === 'prompt') {
        const result = await PushNotifications.requestPermissions();
        if (result.receive !== 'granted') return;
      } else if (permStatus.receive !== 'granted') {
        return;
      }

      // Evita registrar dos veces el listener si el hook se llama varias veces
      await PushNotifications.removeAllListeners();

      PushNotifications.addListener('registration', async (token) => {
        try {
          const plataforma = Capacitor.getPlatform() === 'ios' ? 'ios' : 'android';

          const { error } = await supabase
            .from('push_tokens')
            .upsert(
              {
                id_usuario: idUsuario,
                email,
                token: token.value,
                plataforma,
                activo: true,
              },
              { onConflict: 'id_usuario,token' }
            );

          if (error) throw error;
        } catch (err) {
          Sentry.captureException(err, {
            tags: { action: 'registrar_push_token' },
          });
        }
      });

      PushNotifications.addListener('registrationError', (err) => {
        Sentry.captureException(new Error(JSON.stringify(err)), {
          tags: { action: 'push_registration_error' },
        });
      });

      await PushNotifications.register();
    } catch (err) {
      Sentry.captureException(err, {
        tags: { action: 'solicitar_permiso_push' },
      });
    }
  }, []);

  return { registrarPushToken };
};